import { useCallback, useEffect, useRef, useState } from "react";

import { Box, Typography } from "@mui/material";

import Editor, { BeforeMount, OnMount, OnValidate, useMonaco } from "@monaco-editor/react";
import "@stoplight/mosaic/styles.css";
import dirtyJson from "dirty-json";
import * as Monaco from "monaco-editor/esm/vs/editor/editor.api";

import { useToggle } from "../../hooks";
import { JsonSchemaViewer } from "../json-schema-viewer";
import { ErrorMessageBar } from "./components/error-message-bar";
import { ToolBar } from "./components/tool-bar";
import { downloadJsonFile, minifyJsonString, prettifyJsonString } from "./utils";

// 1) Import the built-in SQL grammar from the monaco-editor package:
import 'monaco-editor/esm/vs/basic-languages/sql/sql.contribution';

const stackStyles = {
  root: {
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    // borderTop: "1px solid #e0e0e0",
    // borderRight: "1px solid #e0e0e0",
    // borderBottom: "1px solid #e0e0e0",
    fontFamily: "Muli,sans-serif",
  },
  editorContainer: {
    // flexGrow: 1,
    flexBasis: "50%",
    display: "flex",
    flexDirection: "column",
    // width: "100%",
    height: "100%",
  },
  editorContainerWrapper: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
  },
  toolbar: {
    borderBottom: "1px solid #e0e0e0",
  },
};

interface JSONEditorProps {
  value: string;
  isSchemaSampleDataOn: boolean;
  language: string;
  schemaValue?: string;
  title?: string;
  path?: string;
  editorReadOnly?: boolean;
  showToolbar?: boolean;
  hideSchemaBox?: boolean;
  onChange: (value: string | undefined) => void;
}

// interface RefObject extends Monaco.editor.ICodeEditor {
//   _domElement?: HTMLElement;
// }

type SelectedVariableType = {
  variable_value: HTMLInputElement | null;
  variable_type: string;
  variable_path: string;
  nodeId: string;
};

const excludeErrorAndSchemaEditor: string[] = ['txt', 'dockerfile', 'sql'];

export const JSONEditor = ({
  value,
  schemaValue,
  title,
  path = "",
  isSchemaSampleDataOn,
  language,
  editorReadOnly = false,
  showToolbar = true,
  hideSchemaBox = false,
  onChange,
}: JSONEditorProps) => {

  const monaco = useMonaco();
  const editorRef = useRef<Monaco.editor.ICodeEditor | null>(null);

  const [errors, setErrors] = useState<string[]>([]);
  const [isAutoPrettifyOn, toggleAutoPrettifyOn] = useToggle(false);
  const [schemaJson, setSchemaJson] = useState<string>("{}");
  const [isValidJson, setIsValidJson] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<SelectedVariableType>({
    variable_value: null,
    variable_type: "",
    variable_path: "",
    nodeId: ""
  });

  const updateEditorLayout = useCallback(() => {
    const editor: any = editorRef.current;
    if (editor) {
      const editorEl = editor._domElement;
      if (editorEl) {
        const { width, height } = editorEl.getBoundingClientRect();
        editor.layout({ width, height });
      }
    }
  }, []);

  const handleJsonSchemasUpdate = useCallback(() => {
    if (schemaValue) {
      const parsedSchemaData = JSON.parse(schemaValue);
      console.log("Parsed schema data:", parsedSchemaData);
      monaco?.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: parsedSchemaData
          ? [
            {
              uri: window.location.href,
              fileMatch: ["*"],
              schema: parsedSchemaData,
            },
          ]
          : undefined,
      });
    }
  }, [schemaValue, monaco]);

  const handleEditorPrettify = useCallback(() => {
    editorRef.current?.getAction("editor.action.formatDocument")?.run();
  }, []);

  const handleEditorUpdateValue = useCallback((value?: string) => {
    const editor = editorRef.current;
    if (editor) {
      editor.setValue(value || "");
      value && editor?.getAction("editor.action.formatDocument")?.run();
    }
  }, []);

  const handleClearClick = () => editorRef.current?.setValue("");

  const handleEditorWillMount: BeforeMount = () => handleJsonSchemasUpdate();

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    editor.getModel()?.updateOptions({ tabSize: 2, insertSpaces: false });
    updateEditorLayout();

    monaco.languages.register({ id: 'sql' })

    window.addEventListener("resize", () => {
      updateEditorLayout();
    });

    // value && handleEditorUpdateValue(prettifyJsonString(value));
  };

  // useEffect(() => {
  //   handleEditorUpdateValue(value);
  // }, [value, handleEditorUpdateValue]);

  useEffect(() => {
    handleJsonSchemasUpdate();
    try {
      if (value && JSON.parse(value)) {
        setSchemaJson(value);
      }
    } catch (e) {
      console.log("Error occured while parsing the json")
    }
  }, [value, handleJsonSchemasUpdate]);

  useEffect(() => {
    updateEditorLayout();
  }, [isSchemaSampleDataOn, updateEditorLayout]);

  useEffect(() => {
    isAutoPrettifyOn && handleEditorPrettify();
  }, [isAutoPrettifyOn, handleEditorPrettify]);

  const handleEditorValidation: OnValidate = useCallback((markers) => {
    console.log('--------markers---------- ', markers)

    const errorMessage = markers.map(
      ({ startLineNumber, message }) => `line ${startLineNumber}: ${message}`
    );
    const hasContent = editorRef.current?.getValue();

    // console.log('has content: ', errorMessage, hasContent)

    const hasError: boolean = errorMessage.length > 0;
    setIsValidJson(!!hasContent && !hasError);
    setErrors(errorMessage);
  }, []);

  const handleMinifyClick = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const value = editor.getValue();
    const minifiedValue = minifyJsonString(value);
    editor.setValue(minifiedValue);
  };

  const handleUploadClick = (file: File) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const result = fileReader.result as string;
      handleEditorUpdateValue(result);
    };
    fileReader.readAsText(file);
  };

  const handleDownloadClick = () => {
    const value = editorRef.current?.getValue();
    value && downloadJsonFile(value);
  };

  const handleEditorChange = useCallback((value: string | undefined) => {
    isAutoPrettifyOn && handleEditorPrettify();
    onChange(value);
  }, [isAutoPrettifyOn, handleEditorPrettify, onChange]);

  const handleFixClick = () => {
    const editor = editorRef.current;
    const value = editor && editor.getValue();
    const fixedValue = value && dirtyJson.parse(value);
    const formattedValue = fixedValue && prettifyJsonString(JSON.stringify(fixedValue));
    editor && editor.setValue(formattedValue);
  };

  return (
    <Box sx={stackStyles.root}>
      {title && (
        <Typography sx={{ m: 2 }} variant="h4">
          {title}
        </Typography>
      )}
      {!excludeErrorAndSchemaEditor.includes(language) && showToolbar && value && (
        <Box sx={stackStyles.toolbar}>
          <ToolBar
            isValidJson={isValidJson}
            isAutoPrettifyOn={isAutoPrettifyOn}
            onAutoPrettifyChange={toggleAutoPrettifyOn}
            onClearClick={handleClearClick}
            onDownloadClick={handleDownloadClick}
            onMinifyClick={handleMinifyClick}
            onPrettifyClick={handleEditorPrettify}
            onUploadClick={handleUploadClick}
            onFixClick={handleFixClick}
            onShowSchemaChange={function (): void {
              throw new Error("Function not implemented.");
            }}
          />
        </Box>
      )}

      {/* <Box sx={stackStyles.editorContainerWrapper}> */}
      <Box sx={{ display: 'flex', height: '100%' }}>
        {/* Left Container */}
        <Box
          sx={{
            // flexBasis: (!excludeErrorAndSchemaEditor.includes(language) && !hideSchemaBox) ? "50%" : "100%",
            // minWidth: 0,
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid",
            borderRightColor: 'divider',
            width: (!excludeErrorAndSchemaEditor.includes(language) && !hideSchemaBox) ? "50%" : "100%",
          }}
        >
          <Box
            sx={{
              height: '80%',
              // flex: "1 1 80%",
              // overflow: "auto",
              // minHeight: 0,
            }}
          >
            <Editor
              language={language}
              path={path}
              options={{
                automaticLayout: true,
                autoClosingBrackets: "always",
                autoClosingQuotes: "always",
                formatOnPaste: true,
                formatOnType: true,
                scrollBeyondLastLine: false,
                readOnly: editorReadOnly,
              }}
              // value={prettifyJsonString(schemaData)}
              value={value}
              onMount={handleEditorDidMount}
              onChange={handleEditorChange}
              beforeMount={handleEditorWillMount}
              onValidate={handleEditorValidation}
            />
          </Box>
          {!excludeErrorAndSchemaEditor.includes(language) &&
            <Box
              sx={{
                height: '20%',
                // flex: "0 1 20%",
                overflow: "auto",
              }}
            >
              {value && <ErrorMessageBar errors={errors} />}
            </Box>}
        </Box>

        {/* Right Container */}
        {!excludeErrorAndSchemaEditor.includes(language) && !hideSchemaBox &&
          <Box
            sx={{
              width: "50%",
              minHeight: "auto",
              overflow: "auto",
              height: "calc(100vh - 142px)",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            <Box
              sx={{
                // flexBasis: "50%",
                // minWidth: 0,
                p: 2,
              }}
            >
              {schemaJson ? (
                <JsonSchemaViewer
                  schema={JSON.parse(schemaJson)}
                  emptyText="Either schema is empty or invalid"
                  defaultExpandedDepth={Infinity}
                  viewMode="read"
                  renderRootTreeLines={true}
                  selectedVariable={selectedVariable}
                  setSelectedVariable={setSelectedVariable}
                />
              ) : (
                <Typography sx={{ m: 2 }}>Invalid or empty schema</Typography>
              )}
            </Box>
          </Box>}
      </Box>
    </Box>
  );
};
